/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * ErrorBoundary — ultima linea di difesa: un errore di rendering in una pagina
 * NON deve mai produrre una schermata bianca. Mostra un fallback elegante con
 * "Riprova" (re-render) e "Torna alla home" (hard navigation).
 *
 * Uso: <ErrorBoundary scope="dashboard"> ... </ErrorBoundary>
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    if (process.env.NODE_ENV !== 'production') {
       
      console.error('[ErrorBoundary]', this.props.scope || 'app', error, info?.componentStack);
    }
    this.props.onError?.(error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback({ error, reset: this.reset });
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    return (
      <div className="min-h-[50vh] flex items-center justify-center p-6" role="alert" data-testid="error-boundary">
        <div className="max-w-md w-full rounded-xl border border-border bg-card p-6 text-center space-y-3">
          <div className="mx-auto h-12 w-12 rounded-full bg-destructive/10 text-destructive flex items-center justify-center"><AlertTriangle className="h-6 w-6" /></div>
          <h2 className="font-display font-semibold text-lg">Questa sezione ha avuto un problema</h2>
          <p className="text-sm text-muted-foreground">
            {offline ? 'Sembra che la connessione sia assente: i dati salvati in locale sono al sicuro e verranno sincronizzati al ritorno della rete.' : 'Il resto dell\'applicazione continua a funzionare. Puoi riprovare oppure tornare alla home.'}
          </p>
          {process.env.NODE_ENV !== 'production' && <pre className="text-[10px] text-left bg-muted/40 rounded-lg p-2 overflow-auto max-h-24 font-mono" data-testid="error-boundary-detail">{String(error?.message || error)}</pre>}
          <div className="flex gap-2 justify-center">
            <Button onClick={this.reset} className="rounded-xl" data-testid="error-boundary-retry"><RefreshCw className="h-4 w-4" /> Riprova</Button>
            <Button variant="outline" onClick={() => { window.location.href = '/'; }} className="rounded-xl" data-testid="error-boundary-home"><Home className="h-4 w-4" /> Home</Button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
